import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import UndoIcon from "@mui/icons-material/Undo";
import { visuallyHidden } from "@mui/utils";
import toast from "react-hot-toast";
import MainCard from "../../../components/MainCard";
import {
  getAccountEntriesApi,
  retrieveAllStakeholderAccountsApi,
  reverseStakeholderAccountEntryApi,
} from "../../../components/api/StakeholderAccountApiService";

// ── Formatters ───────────────────────────────────────────────────────────────

const fmtUsd = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtAmt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 5,
});

function fmt(val) {
  if (val == null) return "—";
  const n = Number(val);
  return isNaN(n) ? "—" : fmtUsd.format(n);
}
function fmtOrig(val) {
  if (val == null || Number(val) === 0) return "—";
  return fmtAmt.format(Number(val));
}
function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("en-GB"); // DD/MM/YYYY
}

// ── Sorting helpers ──────────────────────────────────────────────────────────

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)
    : (a, b) => (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
}

// ── Balance chip ─────────────────────────────────────────────────────────────

function BalanceChip({ value }) {
  const n = Number(value ?? 0);
  const color = n > 0 ? "primary" : n < 0 ? "error" : "default";
  return (
    <Chip
      label={`$${fmt(n)}`}
      color={color}
      size="small"
      variant={n === 0 ? "outlined" : "filled"}
      sx={{ fontWeight: 600, minWidth: 110 }}
    />
  );
}

// ── Entry-type label ─────────────────────────────────────────────────────────

const TYPE_LABELS = {
  SALES_INVOICE:    "Sales Invoice",
  PURCHASE_INVOICE: "Purchase Invoice",
  PAYMENT_RECEIVED: "Payment Received",
  PAYMENT_MADE:     "Payment Made",
  CREDIT_NOTE:      "Credit Note",
  DEBIT_NOTE:       "Debit Note",
  ADVANCE:          "Advance",
};

function EntryTypeChip({ row }) {
  // Color is based on the actual posted side.
  // Debit amounts increase the running balance; credit amounts decrease it.
  const increasesBalance = Number(row?.debitAmountUsd ?? 0) > 0 || Number(row?.debitAmount ?? 0) > 0;
  return (
    <Chip
      label={TYPE_LABELS[row?.entryType] ?? row?.entryType}
      size="small"
      color={increasesBalance ? "warning" : "success"}
      variant="outlined"
      sx={{ fontSize: "0.7rem" }}
    />
  );
}

function StatusChip({ status }) {
  const map = { CONFIRMED: "primary", REVERSED: "error", CANCELLED: "default" };
  return (
    <Chip
      label={status}
      size="small"
      color={map[status] ?? "default"}
      variant="outlined"
      sx={{ fontSize: "0.7rem" }}
    />
  );
}

// ── Ledger Dialog ────────────────────────────────────────────────────────────

const ENTRY_COLS = [
  { id: "entryDate",        label: "Date",          align: "left"  },
  { id: "entryType",        label: "Type",          align: "left"  },
  { id: "referenceNumber",  label: "Invoice #",     align: "left"  },
  { id: "relatedShippingInstructionId", label: "SI", align: "left" },
  { id: "description",      label: "Description",   align: "left"  },
  { id: "currency",         label: "CCY",           align: "center"},
  // A single Amount column — shows the non-zero side (debit or credit) of this entry.
  // Direction is conveyed by the Type chip colour: orange = increases balance, green = decreases.
  { id: "amount",           label: "Amount (Orig)", align: "right" },
  { id: "amountUsd",        label: "Amount (USD)",  align: "right" },
  { id: "runningBalanceUsd",label: "Balance (USD)", align: "right" },
  { id: "fxRateDate",       label: "FX Date",       align: "center"},
  { id: "status",           label: "Status",        align: "center"},
  { id: "actions",          label: "",              align: "center"},
];

function LedgerDialog({ account, open, onClose, onRefreshAccount }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reversing, setReversing] = useState(null);

  useEffect(() => {
    if (open && account) loadEntries();
  }, [open, account]);

  async function loadEntries() {
    try {
      setLoading(true);
      const { data } = await getAccountEntriesApi(account.id);
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load ledger entries.");
    } finally {
      setLoading(false);
    }
  }

  async function handleReverse(entryId) {
    if (!window.confirm("Reverse this entry? This cannot be undone.")) return;
    try {
      setReversing(entryId);
      await reverseStakeholderAccountEntryApi(entryId);
      toast.success("Entry reversed.");
      await loadEntries();
      onRefreshAccount();
    } catch (e) {
      toast.error(e?.response?.data?.message ?? "Failed to reverse entry.");
    } finally {
      setReversing(null);
    }
  }

  const stakeholderName = account?.stakeholder?.name ?? `Account #${account?.id}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.5}>
            <Typography variant="h5" fontWeight={700} color="#1E2685">
              {stakeholderName} — Ledger
            </Typography>
            <Stack direction="row" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Type: <strong>{account?.accountType}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Balance:{" "}
                <strong style={{ color: Number(account?.balanceUsd ?? 0) >= 0 ? "#1E2685" : "#d32f2f" }}>
                  ${fmt(account?.balanceUsd)}
                </strong>
              </Typography>
              {/* Explicit totals: show debit and credit sums directly instead of remapping by account type. */}
              <Typography variant="body2" color="text.secondary">
                Total Debits: {" "}
                <strong>${fmt(account?.totalDebitedUsd)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Credits: {" "}
                <strong>${fmt(account?.totalCreditedUsd)}</strong>
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 1.5 }}>
        {loading ? (
          <Typography align="center" sx={{ py: 4 }} color="text.secondary">Loading entries…</Typography>
        ) : entries.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }} color="text.secondary">No ledger entries yet.</Typography>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {ENTRY_COLS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align}
                      sx={{ fontWeight: 700, whiteSpace: "nowrap", backgroundColor: "#f5f5f5" }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      opacity: row.status === "REVERSED" ? 0.5 : 1,
                      textDecoration: row.status === "REVERSED" ? "line-through" : "none",
                    }}
                  >
                    <TableCell align="left"   sx={{ whiteSpace: "nowrap" }}>{fmtDate(row.entryDate)}</TableCell>
                    <TableCell align="left"><EntryTypeChip row={row} /></TableCell>
                    <TableCell align="left"   sx={{ whiteSpace: "nowrap" }}>{row.referenceNumber ?? "—"}</TableCell>
                    <TableCell align="left"   sx={{ whiteSpace: "nowrap", fontWeight: 500, color: "#1E2685" }}>
                      {row.relatedShippingInstructionId != null ? `#${row.relatedShippingInstructionId}` : "—"}
                    </TableCell>
                    <TableCell align="left"   sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.description ?? "—"}
                    </TableCell>
                    <TableCell align="center">{row.currency ?? "—"}</TableCell>
                    {/* Show whichever side (debit or credit) is non-zero — the type chip already shows the direction */}
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {fmtOrig(Number(row.debitAmount || 0) > 0 ? row.debitAmount : row.creditAmount)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500, color: "#1E2685" }}>
                      {fmtOrig(Number(row.debitAmountUsd || 0) > 0 ? row.debitAmountUsd : row.creditAmountUsd)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>${fmt(row.runningBalanceUsd)}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{fmtDate(row.fxRateDate)}</TableCell>
                    <TableCell align="center"><StatusChip status={row.status} /></TableCell>
                    <TableCell align="center">
                      {row.status === "CONFIRMED" && (
                        <Tooltip title="Reverse this entry">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={reversing === row.id}
                            onClick={() => handleReverse(row.id)}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main account table columns ───────────────────────────────────────────────

const ACCOUNT_COLS = [
  { id: "stakeholderCode", label: "Code",          align: "left"  },
  { id: "stakeholderName", label: "Stakeholder",   align: "left"  },
  { id: "accountType",     label: "Type",          align: "center"},
  { id: "defaultCurrency", label: "CCY",           align: "center"},
  { id: "balanceUsd",      label: "Balance (USD)", align: "right" },
  { id: "totalDebitedUsd", label: "Total Debits (USD)",  align: "right" },
  { id: "totalCreditedUsd", label: "Total Credits (USD)", align: "right" },
  { id: "status",          label: "Status",        align: "center"},
];

// ── StakeholderAccountView ───────────────────────────────────────────────────

export default function StakeholderAccountView() {
  const [accounts, setAccounts]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [typeFilter, setTypeFilter]   = useState("ALL");
  const [order, setOrder]             = useState("asc");
  const [orderBy, setOrderBy]         = useState("stakeholderName");
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected]       = useState(null); // account for ledger dialog

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const type = typeFilter === "ALL" ? undefined : typeFilter;
      const { data } = await retrieveAllStakeholderAccountsApi(type);
      // Flatten stakeholder name/code for sorting
      const flat = (Array.isArray(data) ? data : []).map((a) => ({
        ...a,
        stakeholderName: a.stakeholder?.name ?? "",
        stakeholderCode: a.stakeholder?.code ?? "",
      }));
      setAccounts(flat);
    } catch {
      toast.error("Failed to load stakeholder accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, [typeFilter]);

  // Sorting
  const handleSort = (col) => {
    const isAsc = orderBy === col && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(col);
  };

  const sorted = useMemo(
    () => [...accounts].sort(getComparator(order, orderBy)),
    [accounts, order, orderBy]
  );

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // After a reversal inside the dialog, refresh the account row in the list
  const handleRefreshAccount = async () => {
    await fetchAccounts();
    // Re-select with fresh data so the dialog header updates
    if (selected) {
      const fresh = accounts.find((a) => a.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  };

  return (
    <MainCard>
      {/* ── Toolbar ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 1 }}>
        <Typography variant="h5" fontWeight={700} color="#1E2685">
          Stakeholder Accounts
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={typeFilter}
            onChange={(_, v) => v && setTypeFilter(v)}
          >
            <ToggleButton value="ALL">All</ToggleButton>
            <ToggleButton value="CUSTOMER">Customers</ToggleButton>
            <ToggleButton value="VENDOR">Vendors</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={fetchAccounts} size="small" disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* ── Table ── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {ACCOUNT_COLS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{ fontWeight: 700, whiteSpace: "nowrap", backgroundColor: "#f5f5f5" }}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                    {orderBy === col.id && (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </Box>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={ACCOUNT_COLS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading…</Typography>
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={ACCOUNT_COLS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No accounts found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => setSelected(row)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{row.stakeholderCode || "—"}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{row.stakeholderName || "—"}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.accountType}
                      size="small"
                      color={row.accountType === "CUSTOMER" ? "primary" : "secondary"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">{row.defaultCurrency ?? "USD"}</TableCell>
                  <TableCell align="right">
                    <BalanceChip value={row.balanceUsd} />
                  </TableCell>
                  {/* Show the raw total debit and credit sums directly. */}
                  <TableCell align="right" sx={{ color: "#e65100" }}>
                    ${fmt(row.totalDebitedUsd)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "#2e7d32" }}>
                    ${fmt(row.totalCreditedUsd)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.status}
                      size="small"
                      color={row.status === "ACTIVE" ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={sorted.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* ── Ledger Dialog ── */}
      <LedgerDialog
        account={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onRefreshAccount={handleRefreshAccount}
      />
    </MainCard>
  );
}
